import { Link } from 'react-router-dom';
import { Trash2 } from '@/components/ui/icons';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { resolveApiMediaUrl } from '@/lib/media';
import { Badge } from '@/components/ui/Badge';
import { IconButton } from '@/components/ui/IconButton';
import { PriceBlock } from '@/components/ui/PriceBlock';
import { QuantityStepper } from '@/components/ui/QuantityStepper';
import { cartItemCardStyles } from './CartItemCard.styles';

export type CartItemCardProps = {
  imageUrl: string;
  name: string;
  productHref?: string;
  unitPrice: string;
  lineTotal?: string;
  quantity: number;
  onQuantityChange: (next: number) => void;
  onRemove: () => void;
  disabled?: boolean;
  isValid?: boolean;
  validationMessage?: string;
  meta?: ReactNode;
  sellerBadge?: { label: string; variant?: 'brand' | 'neutral' };
  removeLabel: string;
  decreaseLabel: string;
  increaseLabel: string;
  className?: string;
};

export function CartItemCard({
  imageUrl,
  name,
  productHref,
  unitPrice,
  lineTotal,
  quantity,
  onQuantityChange,
  onRemove,
  disabled = false,
  isValid = true,
  validationMessage,
  meta,
  sellerBadge,
  removeLabel,
  decreaseLabel,
  increaseLabel,
  className,
}: CartItemCardProps): JSX.Element {
  const resolvedImageUrl = resolveApiMediaUrl(imageUrl) ?? imageUrl;
  const title = productHref ? (
    <Link to={productHref} className={cn(cartItemCardStyles.title, cartItemCardStyles.titleLink)}>
      {name}
    </Link>
  ) : (
    <h3 className={cartItemCardStyles.title}>{name}</h3>
  );

  return (
    <div
      className={cn(
        cartItemCardStyles.root,
        !isValid && cartItemCardStyles.rootInvalid,
        className,
      )}
    >
      <div className={cartItemCardStyles.layout}>
        <div className={cartItemCardStyles.thumb}>
          <img src={resolvedImageUrl} alt="" className={cartItemCardStyles.thumbImg} />
        </div>
        <div className={cartItemCardStyles.body}>
          <div className={cartItemCardStyles.top}>
            <div className="min-w-0">
              {title}
              {sellerBadge || meta ? (
                <div className={cartItemCardStyles.meta}>
                  {sellerBadge ? (
                    <div>
                      <Badge variant={sellerBadge.variant ?? 'neutral'} className="mr-2">
                        {sellerBadge.label}
                      </Badge>
                    </div>
                  ) : null}
                  {meta}
                </div>
              ) : null}
            </div>
            <IconButton
              type="button"
              size="sm"
              variant="danger"
              aria-label={removeLabel}
              disabled={disabled}
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </IconButton>
          </div>

          {!isValid && validationMessage ? (
            <div className={cartItemCardStyles.invalid}>{validationMessage}</div>
          ) : null}

          <div className={cartItemCardStyles.actions}>
            <div className={cartItemCardStyles.qtyPrice}>
              <QuantityStepper
                value={quantity}
                onChange={onQuantityChange}
                disabled={disabled || !isValid}
                decreaseLabel={decreaseLabel}
                increaseLabel={increaseLabel}
              />
              <div className={cartItemCardStyles.priceCol}>
                <PriceBlock amount={unitPrice} size="sm" />
                {lineTotal ? <span className={cartItemCardStyles.subtotal}>{lineTotal}</span> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
